import { Injectable, OnDestroy } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { SignalRService } from './signalr.service';
import {
  LoadcellBoardEvent,
  CardUpdatedEvent,
  SwimlaneChangedEvent,
  WorkOrderUpdatedEvent,
} from './signalr.models';

@Injectable({
  providedIn: 'root',
})
export class BoardHubService implements OnDestroy {
  // Event streams
  loadcellsUpdated$ = new Subject<LoadcellBoardEvent>();
  cardUpdated$ = new Subject<CardUpdatedEvent>();
  swimlaneChanged$ = new Subject<SwimlaneChangedEvent>();
  workOrderUpdated$ = new Subject<WorkOrderUpdatedEvent>();

  private activeGroups = new Set<string>();
  private listenersRegistered = false;
  private reconnectSub: Subscription;

  constructor(private signalR: SignalRService) {
    this.reconnectSub = this.signalR.reconnected$.subscribe(() => {
      this.rejoinGroups();
    });
  }

  private async ensureConnected(): Promise<void> {
    await this.signalR.start();
    this.registerListeners();
  }

  private registerListeners(): void {
    if (this.listenersRegistered) return;
    this.listenersRegistered = true;

    const conn = this.signalR.getConnection();

    conn.on('LoadcellsUpdated', (event: LoadcellBoardEvent) => {
      this.loadcellsUpdated$.next(event);
    });

    conn.on('CardUpdated', (event: CardUpdatedEvent) => {
      this.cardUpdated$.next(event);
    });

    conn.on('SwimlaneChanged', (event: SwimlaneChangedEvent) => {
      this.swimlaneChanged$.next(event);
    });

    conn.on('WorkOrderUpdated', (event: WorkOrderUpdatedEvent) => {
      this.workOrderUpdated$.next(event);
    });
  }

  // DLM Loadcell board — shared by Workshop Dashboard and Servicing
  async joinLoadcellBoard(): Promise<void> {
    await this.ensureConnected();
    await this.signalR.getConnection().invoke('JoinLoadcellBoard');
    this.activeGroups.add('loadcell-board');
  }

  async leaveLoadcellBoard(): Promise<void> {
    this.activeGroups.delete('loadcell-board');
    try {
      await this.signalR.getConnection().invoke('LeaveLoadcellBoard');
    } catch {
      // Connection may already be closed during destroy
    }
  }

  // Scrum / Asset boards
  async joinBoard(id: number): Promise<void> {
    await this.ensureConnected();
    await this.signalR.getConnection().invoke('JoinBoard', id);
    this.activeGroups.add(`board-${id}`);
  }

  async leaveBoard(id: number): Promise<void> {
    this.activeGroups.delete(`board-${id}`);
    try {
      await this.signalR.getConnection().invoke('LeaveBoard', id);
    } catch {
      // Connection may already be closed during destroy
    }
  }

  // Work Order board
  async joinWorkOrderBoard(): Promise<void> {
    await this.ensureConnected();
    await this.signalR.getConnection().invoke('JoinWorkOrderBoard');
    this.activeGroups.add('work-order-board');
  }

  async leaveWorkOrderBoard(): Promise<void> {
    this.activeGroups.delete('work-order-board');
    try {
      await this.signalR.getConnection().invoke('LeaveWorkOrderBoard');
    } catch {
      // Connection may already be closed during destroy
    }
  }

  private async rejoinGroups(): Promise<void> {
    const conn = this.signalR.getConnection();
    for (const group of this.activeGroups) {
      try {
        if (group === 'loadcell-board') {
          await conn.invoke('JoinLoadcellBoard');
        } else if (group === 'work-order-board') {
          await conn.invoke('JoinWorkOrderBoard');
        } else if (group.startsWith('board-')) {
          const id = parseInt(group.replace('board-', ''), 10);
          await conn.invoke('JoinBoard', id);
        }
      } catch {
        // Rejoin failed — will retry on next reconnect
      }
    }
  }

  ngOnDestroy(): void {
    this.reconnectSub?.unsubscribe();
  }
}
